import _ from "lodash";

import { SessionStore } from "../uidispatcherapi/SessionStore";
import React from "react";

import { isTier12Customer } from "./SessionStore";
import CustomerAddAddressAction from "../actions/CustomerAddAddressAction";
import CustomerDeleteAddressAction from "../actions/CustomerDeleteAddressAction";
import { Customer, transformCustomer } from "../datatypes/customer/Customer";
import { setCustomerInformation, trackUiErrorCa } from "../utils/TrackingUtil";
import { deleteWebShopAddress, getCustomer, postWebShopAddress } from "../clients/CustomerServiceClient";
import AddressValidationStore from "./AddressValidationStore";
import { Store } from "../utils/Store";
import { AddressValidation } from "../datatypes/customer/AddressValidation";
import { AddressType } from "../datatypes/Address";
import { NotificationHandle, notify } from "../utils/Notifications";
import { FormattedMessage } from "react-intl";

// maps from customerId -> data
let failed: {
    [customerId: string]: boolean;
} = {};
let customers: {
    [customerId: string]: Customer | undefined | null;
} = {};
let alreadyLoading: {
    [customerId: string]: boolean;
} = {};

/**
 * Contains data for the CURRENT and already loaded customers.
 */
class CustomerStoreImpl extends Store {
    constructor() {
        super();
        this.listenTo(SessionStore, this.onSessionUpdate);
        this.listenTo(CustomerAddAddressAction, this.onCustomerAddAddress as any);
        this.listenTo(CustomerDeleteAddressAction, this.onCustomerDeleteAddress as any);
        this.onSessionUpdate();
    }

    onCustomerAddAddress(address: AddressType, doRedirect: () => void, isAlreadyValidated: boolean) {
        if (isAlreadyValidated) {
            this.addCustomerAddress(address, doRedirect);
        } else {
            AddressValidationStore.reset();
            AddressValidationStore.validateWebShopAddress(address, (val: AddressValidation | undefined | null) => {
                const country = _.get(val, "country");
                if (!country) {
                    return;
                }
                const shortName = _.get(country.suggestion, "shortName");
                if (country.valid && shortName) {
                    address.country = shortName;
                }
                const updatedAddress =
                    country.valid && shortName ? _.assign({}, address, { country: shortName }) : address;
                this.addCustomerAddress(updatedAddress, doRedirect);
            });
        }
    }

    addCustomerAddress(address: AddressType, doRedirect: () => void): void {
        const customerId: string | undefined | null = SessionStore.getCustomerId();

        const onSuccess = (response: any) => {
            const notification = {
                type: "success" as const,
                key: "EX_ADD_ADDRESS_SUCCESS",
                values: [],
                id: "EX_ADD_ADDRESS_SUCCESS",
                timeout: 5000
            };
            notify(notification);
            doRedirect();
            return this._handleCustomerData(response);
        };

        const onFail = (e: any) => {
            const body = e.responseText || "";
            let json = {} as any;
            try {
                json = JSON.parse(body);
            } catch (e) {
                // nothing to do
            }
            if (_.startsWith(body, "BTEX-CA0001")) {
                const notification = {
                    type: "warning" as const,
                    key: "EX_ADD_ADDRESS_ERROR_TOO_MANY",
                    values: [],
                    id: "EX_ADD_ADDRESS_ERROR_TOO_MANY",
                    timeout: 5000
                };
                notify(notification);
            } else if (_.startsWith(body, "BTEX-CA00V7")) {
                const notification = {
                    type: "warning" as const,
                    key: "EX_ADD_ADDRESS_ERROR_FIELD_TOO_LONG",
                    values: [],
                    id: "EX_ADD_ADDRESS_ERROR_FIELD_TOO_LONG",
                    timeout: 5000
                };
                notify(notification);
                // CA00V1 - CA00V6 are country, postcode, line1, line2, city and type
            } else if (_.startsWith(body, "BTEX-CA00V")) {
                const notification = {
                    type: "warning" as const,
                    key: "EX_ADD_ADDRESS_ERROR_MISSING_FIELD",
                    values: [],
                    id: "EX_ADD_ADDRESS_ERROR_MISSING_FIELD",
                    timeout: 5000
                };
                notify(notification);
            } else if (json.code === "country-not-allowed") {
                const notification = {
                    type: "warning" as const,
                    key: "EX_ADD_ADDRESS_ERROR_BAD_COUNTRY",
                    values: [],
                    id: "EX_ADD_ADDRESS_ERROR_BAD_COUNTRY",
                    timeout: 5000
                };
                notify(notification);
            } else {
                const notification = {
                    type: "warning" as const,
                    key: "EX_ADD_ADDRESS_ERROR",
                    values: [],
                    id: "EX_ADD_ADDRESS_ERROR",
                    timeout: 5000
                };
                notify(notification);
            }

            trackUiErrorCa("CustomerStore", "Add Address Failed.", e);
            this.trigger();
        };

        if (customerId) {
            postWebShopAddress(customerId, address).then(onSuccess).catch(onFail);
        }
    }

    onCustomerDeleteAddress(address: AddressType): void {
        const customerId: string | undefined | null = SessionStore.getCustomerId();

        const onSuccess = (response: any) => {
            let notificationHandler: NotificationHandle | null = null;
            const callbackProps = {
                callback: () => {
                    if (notificationHandler) notificationHandler.dismiss();
                    this.onCustomerAddAddress(address, () => {}, true);
                },
                label: "EX_DELETE_ADDRESS_UNDO"
            };
            const notification = {
                type: "success" as const,
                key: "EX_DELETE_ADDRESS_SUCCESS",
                values: [],
                id: "EX_DELETE_ADDRESS_SUCCESS",
                extras: (
                    <a href={"#"} onClick={callbackProps.callback}>
                        <FormattedMessage id={callbackProps.label} />
                    </a>
                ),
                timeout: 5000
            };
            notificationHandler = notify(notification);
            return this._handleCustomerData(response);
        };

        const onFail = (e: any) => {
            const body = e.responseText || "";
            if (_.startsWith(body, "BTEX-CA0002")) {
                const notification = {
                    type: "warning" as any,
                    key: "EX_DELETE_ADDRESS_ERROR_NOT_FOUND",
                    values: [],
                    id: "EX_DELETE_ADDRESS_ERROR_NOT_FOUND",
                    timeout: 5000
                };
                notify(notification);
            } else {
                const notification = {
                    type: "warning" as any,
                    key: "EX_DELETE_ADDRESS_ERROR",
                    values: [],
                    id: "EX_DELETE_ADDRESS_ERROR",
                    timeout: 5000
                };
                notify(notification);
            }

            trackUiErrorCa("CustomerStore", "Delete Address Failed.", e);
            this.trigger();
        };

        if (customerId && address.id) {
            deleteWebShopAddress(customerId, address.id).then(onSuccess).catch(onFail);
        }
    }

    async loadCustomer(customerId: string) {
        if (alreadyLoading[customerId] || customers[customerId] || failed[customerId]) {
            if (customers[customerId]) {
                this.trigger(customers[customerId]);
            }
            // do nothing, as customer is currently loading / loaded / failed to load
            return;
        }
        alreadyLoading[customerId] = true;
        try {
            const response = await getCustomer(customerId);
            this._handleCustomerData(response);
        } catch (e) {
            this._handleCustomerLoadFailed(e, customerId);
        }
    }

    onSessionUpdate() {
        if (isTier12Customer()) {
            return;
        }
        const newCustomerId: string | undefined | null = SessionStore.getCustomerId();
        if (newCustomerId) {
            this.loadCustomer(newCustomerId);
        }
    }

    getDeliveryStoresForCurrentCustomer(): string[] {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        if (!customer) {
            return [];
        }
        return _.map(customer.deliveryAddresses, (address) => {
            return address.deliveryStore;
        });
    }

    isCurrentCustomerFoodCustomer() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.food : null;
    }

    isCurrentCustomerFsdCustomer() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.validForFsd : false;
    }

    isCurrentCustomerFsdCustomerInSelectedStore() {
        if (!this.isCurrentCustomerFsdCustomer()) {
            return false;
        }
        const fsdStores = this.getDeliveryStoresForCurrentCustomer();
        const currStore = SessionStore.getCustomerStore();
        return _.includes(fsdStores, currStore);
    }

    _handleCustomerLoadFailed(e: unknown, customerId: string): void {
        trackUiErrorCa("CustomerStore", "Load Customer failed.", e);
        failed[customerId] = true;
        alreadyLoading[customerId] = false;
        this.trigger(null);
    }

    isFailed(customerId?: string | undefined | null) {
        if (customerId) {
            return failed[customerId];
        } else {
            return failed[SessionStore.getCustomerId()];
        }
    }

    isLoading() {
        return (
            SessionStore.pending() || !!(SessionStore.getCustomerId() && alreadyLoading[SessionStore.getCustomerId()])
        );
    }

    _handleCustomerData(response: any): void {
        let customer;
        const customerId: string = response.customerId;
        try {
            customers[customerId] = transformCustomer(response);
            customer = customers[customerId];
            failed[customerId] = false;
            alreadyLoading[customerId] = false;
        } catch (error) {
            this._handleCustomerLoadFailed(error, customerId);
            return;
        }

        // Set Customer info for tracking
        setCustomerInformation(customerId, customer);

        if (customer && SessionStore.getCustomerId() === customerId) {
            if (_.isNil(SessionStore.getCardholderNumber())) {
                // just in case the cardholderNumber was not set.
                SessionStore.setSelectedCustomerId(customerId, customer.primaryCardholderNumber);
            }

            const addressId: string | undefined | null = SessionStore.getSelectedDeliveryAddressId();
            if (addressId) {
                const addresses = customer.deliveryAddresses;
                const foundAddress = _.find(addresses, { integrationId: addressId });
                if (foundAddress) {
                    SessionStore.setDeliveryAddress(foundAddress);
                } else {
                    console.warn(
                        "did not find address in store and in customer, even though there was an address id: ",
                        addressId
                    );
                    SessionStore.setDeliveryAddress(null);
                }
            }
        }
        // Trigger customer without order count
        // We do that, because, in case that order service is not available, we still want to trigger the customer data.
        this.trigger(customer);
    }

    getCustomer(): Customer | undefined | null {
        return customers[SessionStore.getCustomerId()];
    }

    getCustomerStoreSelectorSettings() {
        return {
            isFsdCustomer: this.isCurrentCustomerFsdCustomer(),
            deliveryStores: this.getDeliveryStoresForCurrentCustomer()
        };
    }

    getLoggedInName() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.customerName : null;
    }

    getLoggedInCustomerDisplayNumber() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.displayNumber : null;
    }

    getLoggedInCustomerEmail() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.email : null;
    }

    getLoggedInCustomerMainPhone() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.mainPhone : null;
    }

    // For testing
    resetStore() {
        failed = {};
        customers = {};
        alreadyLoading = {};
    }
}
export default new CustomerStoreImpl();
